import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Alert, ConfigProvider, message, Modal } from 'antd';
import { StopOutlined } from '@ant-design/icons';
import { WizardLayout, DERMATO_FLOW, DERMATO_STEPS, type WizardPatientSummary } from '../../components/wizard';
import { useDermatoWizard, DermatoWizardProvider } from './wizard/DermatoWizardContext';
import { DERMATO_STEP_COMPONENTS } from './wizard/dermatoSteps';
import { useAuthStore } from '../../store/authStore';
import ExportActions from './ExportActions';
import FlowPanel, { useFlowState } from '../../components/flow/FlowPanel';
import {
  DERMATO_STEP_KEYS,
  DERMATO_LESION_SCOPED_KEYS,
  DERMATO_SCREEN_BY_FLOW_INDEX,
  DERMATO_PRIMARY_FLOW_INDEX_BY_SCREEN,
  DERMATO_PHASE_ONLY_FLOW_INDEXES,
  DERMATO_SCREEN_COUNT,
} from './wizard/dermatoStepKeys';
import { ScreeningConclusion } from '../../api/dermatoApi';

const calculateAge = (dateOfBirth: string): number | undefined => {
  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return undefined;
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) {
    age -= 1;
  }
  return age;
};

/**
 * Parametrul :step din rută e 1-based (ca în UI: /dermato-wizard/4 = pasul 4).
 * Returnează indexul 0-based sau null dacă parametrul lipsește/e invalid.
 */
const parseStepParam = (raw?: string): number | null => {
  if (!raw) return null;
  const n = Number(raw);
  if (Number.isNaN(n)) return null;
  const idx = Math.round(n) - 1;
  return idx >= 0 && idx < DERMATO_FLOW.totalSteps ? idx : null;
};

const NO_LESIONS_MESSAGE = 'Adaugă mai întâi cel puțin o leziune la pasul 4 (Leziuni cutanate).';
/**
 * Pașii care au nevoie de o leziune: evaluările per leziune (ABCDE, dermatoscopie,
 * video AI) și pașii de investigare/tratament/follow-up. Ramura benignă
 * („Recontrol programat") e și ea per-leziune — sursa medicală (PAS 10 „Leziune
 * benignă") o descrie per leziune, nu per pacient.
 */
const LESION_DEPENDENT_STEP_KEYS = new Set([...DERMATO_LESION_SCOPED_KEYS, 'screeningNegative']);
/** Pasul „Leziuni cutanate" (index 0-based) — destinația redirectării. */
const LESIONS_STEP_INDEX = 3;

/**
 * Ecranul (0-based) asociat unui pas de flux (0-based).
 * Motorul de flux are 18 pași, wizardul are 15 ecrane — vezi
 * `DERMATO_SCREEN_BY_FLOW_INDEX`.
 */
const screenOf = (flowIndex: number): number => DERMATO_SCREEN_BY_FLOW_INDEX[flowIndex] ?? 0;
/**
 * Pasul de flux „primar" al unui ecran: singurul care devine „pasul curent"
 * (titlu + număr în antet, URL, bifă în sidebar). Ceilalți pași de flux care
 * împart ecranul (markerii de fază `screeningNegative` și `closed`, plus
 * `outcome`) rămân în sidebar ca etichete de fază.
 */
const primaryOf = (flowIndex: number): number =>
  DERMATO_PRIMARY_FLOW_INDEX_BY_SCREEN[screenOf(flowIndex)] ?? flowIndex;

const DermatoWizardInner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: patientId, step: stepParam } = useParams<{ id: string; step?: string }>();
  const { user } = useAuthStore();
  const ctx = useDermatoWizard();

  const [currentStep, setCurrentStep] = useState(() => parseStepParam(stepParam) ?? 0);
  const [savingDraft, setSavingDraft] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const currentStepRef = useRef(currentStep);
  currentStepRef.current = currentStep;

  const hasLesions = ctx.lesions.length > 0;

  // Starea fluxului (fază, drepturi pe pas, tranziții, sarcini) — decisă de server după rolul utilizatorului.
  const flow = useFlowState('dermato', ctx.form?.id);
  // Ecranul curent (0-based) — pasul de flux poate fi un marker de fază.
  const screenIndex = screenOf(currentStep);
  const stepKeys = DERMATO_STEP_KEYS[screenIndex] ?? [];
  const lesionScoped = stepKeys.some((k) => DERMATO_LESION_SCOPED_KEYS.has(k));
  const stateLesionId = (location.state as { lesionId?: string } | null)?.lesionId;
  const flowScopeId = lesionScoped
    ? ctx.selectedLesionId ?? (ctx.lesions.length === 1 ? ctx.lesions[0].id : undefined)
    : undefined;
  const currentFlowSteps = (flow.state?.steps ?? []).filter((st) => stepKeys.includes(st.key));
  // 2.9.3 — refuzul participării închide episodul: totul e doar pentru citire.
  const refused =
    ctx.screeningResult?.screeningConclusion === ScreeningConclusion.SCREENING_REFUZAT;
  const readOnly =
    refused || (currentFlowSteps.length > 0 && currentFlowSteps.every((st) => !st.canWrite));
  const readOnlyRef = useRef(readOnly);
  readOnlyRef.current = readOnly;

  // Fără leziuni, pașii care depind de o leziune sunt blocați; rămân accesibile
  // pasul „Rezultat screening" (index 7) și cei de înrolare/chestionar.
  // După refuz (EXCLUS DIN PROGRAM) e blocat totul în afară de înrolare.
  const disabledSteps = useMemo(() => {
    // 2.9.3 — episod exclus din program: rămâne accesibil doar pasul de înrolare
    // (unde se consemnează acceptul/refuzul), restul e blocat.
    if (refused) {
      return DERMATO_STEPS.map((_, index) => index).filter((index) => index !== 0);
    }
    return hasLesions
      ? []
      : DERMATO_STEPS.map((s, index) => ({ key: s.key, index }))
          .filter(({ key }) => !!key && LESION_DEPENDENT_STEP_KEYS.has(key))
          .map(({ index }) => index);
  }, [hasLesions, refused]);

  /**
   * Scrie pasul în URL (1-based), păstrând location.state (editFormId).
   * Indexul primit e un pas de flux; îl normalizăm la pasul „primar" al
   * ecranului aferent (markerii de fază nu sunt pași curenți).
   */
  const navigateToStep = useCallback(
    (index: number) => {
      const target = primaryOf(index);
      setCurrentStep(target);
      ctx.resetStepDirty();
      navigate(`/patients/${patientId}/dermato-wizard/${target + 1}`, {
        replace: true,
        state: location.state,
      });
      // Progresul (bifele) se reîmprospătează la fiecare schimbare de pas —
      // acoperă și salvările făcute prin butoanele interne ale pașilor.
      void ctx.refreshProgress();
      void flow.reload();
    },
    [navigate, patientId, location.state, ctx, flow]
  );

  // Sincronizare URL → stare la navigare externă (editare manuală URL / mount cu :step).
  useEffect(() => {
    const idx = parseStepParam(stepParam);
    if (idx !== null) {
      const target = primaryOf(idx);
      if (target !== currentStepRef.current) {
        setCurrentStep(target);
        ctx.resetStepDirty();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepParam]);

  // Deschis dintr-o sarcină: preselectăm leziunea sarcinii (scopeId).
  useEffect(() => {
    if (ctx.loading || !stateLesionId || ctx.selectedLesionId) return;
    if (ctx.lesions.some((l) => l.id === stateLesionId)) ctx.selectLesion(stateLesionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.loading, ctx.lesions, stateLesionId]);

  // Gating la intrarea directă pe URL: un pas care depinde de leziune, fără leziuni → pasul „Leziuni cutanate".
  useEffect(() => {
    if (ctx.loading) return;
    if (!refused && !hasLesions && disabledSteps.includes(currentStepRef.current)) {
      message.warning(NO_LESIONS_MESSAGE);
      navigateToStep(LESIONS_STEP_INDEX);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.loading, hasLesions, refused]);

  // 2.9.3 — episodul exclus din program: singurul pas accesibil e înrolarea
  // (unde se consemnează decizia de accept/refuz).
  useEffect(() => {
    if (ctx.loading || !refused) return;
    if (currentStepRef.current !== 0) navigateToStep(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.loading, refused]);

  // Avertisment la închiderea paginii cu modificări nesalvate.
  useEffect(() => {
    if (!ctx.stepDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [ctx.stepDirty]);

  /**
   * Navigare gardată: mai întâi se salvează pasul curent prin handler-ul real
   * al pasului. Dacă salvarea eșuează (validare/eroare), utilizatorul alege
   * explicit între a rămâne și a continua fără salvare.
   */
  const guardedGoTo = useCallback(
    async (index: number) => {
      if (index === currentStepRef.current) return;
      if (index < 0 || index >= DERMATO_FLOW.totalSteps) return;
      if (disabledSteps.includes(index)) {
        message.warning(NO_LESIONS_MESSAGE);
        return;
      }
      const saved = readOnlyRef.current ? true : await ctx.runStepSave();
      if (saved) {
        navigateToStep(index);
        return;
      }
      Modal.confirm({
        title: 'Modificări nesalvate',
        content:
          'Datele de pe pasul curent nu au putut fi salvate (verifică erorile evidențiate în formular). ' +
          'Dacă continui, modificările nesalvate se pierd.',
        okText: 'Continuă fără salvare',
        okButtonProps: { danger: true },
        cancelText: 'Rămâi pe pas',
        onOk: () => navigateToStep(index),
      });
    },
    [ctx, disabledSteps, navigateToStep]
  );

  const handlePrev = useCallback(() => {
    // Navigarea se face pe ecrane (nu pe pași de flux) — markerii de fază
    // (`screeningNegative`, `closed`) nu sunt pași navigabili.
    const prevScreen = screenOf(currentStepRef.current) - 1;
    if (prevScreen < 0) return;
    void guardedGoTo(DERMATO_PRIMARY_FLOW_INDEX_BY_SCREEN[prevScreen]);
  }, [guardedGoTo]);

  const handleNext = useCallback(async () => {
    const nextScreen = Math.min(DERMATO_SCREEN_COUNT - 1, screenOf(currentStepRef.current) + 1);
    const target = DERMATO_PRIMARY_FLOW_INDEX_BY_SCREEN[nextScreen];
    if (disabledSteps.includes(target)) {
      message.warning(NO_LESIONS_MESSAGE);
      return;
    }
    try {
      setAdvancing(true);
      // Pasul curent își salvează datele prin handler-ul înregistrat în context;
      // avansăm doar dacă salvarea a reușit (sau pasul nu are ce salva).
      const saved = readOnlyRef.current ? true : await ctx.runStepSave();
      if (!saved) return;
      navigateToStep(target);
    } finally {
      setAdvancing(false);
    }
  }, [ctx, disabledSteps, navigateToStep]);

  const handleSaveDraft = useCallback(async () => {
    if (readOnlyRef.current) {
      message.info('Pasul este doar pentru citire pentru rolul tău.');
      return;
    }
    try {
      setSavingDraft(true);
      await ctx.saveDraft();
    } finally {
      setSavingDraft(false);
    }
  }, [ctx]);

  const incompleteSteps = useMemo(() => {
    const missing: string[] = [];
    const f = ctx.form;
    const hasAnamnesis = Boolean(
      f && (
        f.personalHistory
        || Object.keys(f.medicalHistoryDetails ?? {}).length > 0
        || Object.keys(f.familyHistoryDetails ?? {}).length > 0
        || Object.keys(f.uvRiskFactors ?? {}).length > 0
      )
    );
    if (!hasAnamnesis) {
      missing.push('Pasul 2 (Anamneză / Risc) — niciun factor completat');
    }
    if (ctx.lesions.length === 0) {
      missing.push('Nicio leziune înregistrată — pașii 4–15 (per leziune) nu conțin date');
    }
    return missing;
  }, [ctx.form, ctx.lesions]);

  const handleSubmit = useCallback(() => {
    Modal.confirm({
      title: 'Finalizezi chestionarul dermatologic?',
      content: (
        <div>
          <p>
            După finalizare formularul este trimis spre revizuire și devine read-only —
            nu vei mai putea modifica datele.
          </p>
          {incompleteSteps.length > 0 && (
            <div>
              <p>Atenție — secțiuni fără date:</p>
              <ul>
                {incompleteSteps.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ),
      okText: 'Da, finalizează',
      cancelText: 'Anulează',
      onOk: async () => {
        try {
          setSubmitting(true);
          const ok = await ctx.submit();
          if (ok && ctx.patient) {
            navigate(`/patients/${ctx.patient.id}`);
          }
        } finally {
          setSubmitting(false);
        }
      },
    });
  }, [ctx, navigate, incompleteSteps]);

  const patientSummary = useMemo<WizardPatientSummary | null>(() => {
    if (!ctx.patient) return null;
    const age = calculateAge(ctx.patient.dateOfBirth);
    const sexLabel = ctx.patient.sex === 'M' ? 'Masculin' : ctx.patient.sex === 'F' ? 'Feminin' : undefined;
    const programLabel = ctx.patient.programType === 'DERMATO'
      ? 'RO – Timișoara (Cutanat)'
      : ctx.patient.programType === 'HEPATO'
        ? 'RO – Timișoara (Hepatic)'
        : 'Ambele programe';
    const completed = ctx.progress?.completedCount;
    return {
      id: ctx.patient.id,
      fullName: `${ctx.patient.lastName} ${ctx.patient.firstName}`.trim(),
      age,
      sexLabel,
      programLabel,
      statusLabel: refused
        ? 'Exclus din program'
        : ctx.form?.status === 'DRAFT'
          ? 'În curs'
          : ctx.form?.status ?? '—',
      statusTagColor: refused ? 'red' : ctx.form?.status === 'DRAFT' ? 'blue' : 'green',
      extraLines: [
        `Form ID: ${ctx.form?.id?.slice(0, 8) ?? '—'}`,
        `Leziuni: ${ctx.lesions.length}`,
        completed !== undefined ? `Pași completați: ${completed} / ${DERMATO_FLOW.totalSteps}` : '',
        ctx.form ? `Ultima actualizare: ${new Date(ctx.form.updatedAt).toLocaleString('ro-RO')}` : '',
      ].filter(Boolean),
    };
  }, [ctx.patient, ctx.form, ctx.lesions, ctx.progress, refused]);

  if (ctx.error && !patientSummary) {
    return (
      <div style={{ padding: 24 }}>
        <h2>Eroare</h2>
        <p>{ctx.error}</p>
        <button type="button" onClick={() => navigate('/patients')}>Înapoi la pacienți</button>
      </div>
    );
  }

  if (!patientSummary) {
    return (
      <WizardLayout
        flow={DERMATO_FLOW}
        currentStep={0}
        onGoToStep={() => undefined}
        onPrev={() => undefined}
        onNext={() => undefined}
        onSaveDraft={async () => undefined}
        patientSummary={{
          id: '',
          fullName: 'Se încarcă…',
          statusLabel: '—',
          statusTagColor: 'default',
        }}
        loading
      />
    );
  }

  const StepComponent = DERMATO_STEP_COMPONENTS[screenIndex] ?? DERMATO_STEP_COMPONENTS[0];

  return (
    <div>
      {refused && (
        <Alert
          type="error"
          showIcon
          icon={<StopOutlined />}
          style={{ marginBottom: 12 }}
          data-testid="excluded-banner"
          message="EXCLUS DIN PROGRAM — pacientul a refuzat participarea la screening"
          description="Episodul a fost închis ca refuzat (starea SCREENING REFUZAT). Pașii de evaluare nu mai sunt accesibili; pentru reevaluare se deschide un episod nou."
        />
      )}
      <ExportActions
        formId={ctx.form?.id ?? null}
        lesions={ctx.lesions}
        selectedLesionId={ctx.selectedLesionId}
        onSelectLesion={ctx.selectLesion}
      />
      {ctx.form?.id && (
        <FlowPanel
          kind="dermato"
          formId={ctx.form.id}
          state={flow.state}
          stepKeys={stepKeys}
          scopeId={flowScopeId}
          scopeMissing={lesionScoped && !flowScopeId}
          onChanged={flow.reload}
        />
      )}
      <WizardLayout
        flow={DERMATO_FLOW}
        currentStep={currentStep}
        onGoToStep={(index) => void guardedGoTo(index)}
        onPrev={handlePrev}
        onNext={handleNext}
        onSaveDraft={handleSaveDraft}
        onSubmit={handleSubmit}
        savingDraft={savingDraft}
        nextLoading={advancing}
        isSubmitting={submitting}
        patientSummary={patientSummary}
        loading={ctx.loading}
        nextLabel="Salvează și continuă"
        submitLabel={`Finalizează (${user?.firstName ?? 'utilizator'})`}
        disabledSteps={disabledSteps}
        phaseOnlyStepIndexes={DERMATO_PHASE_ONLY_FLOW_INDEXES}
        lastStepIndex={DERMATO_PRIMARY_FLOW_INDEX_BY_SCREEN[DERMATO_SCREEN_COUNT - 1]}
        disabledReason={NO_LESIONS_MESSAGE}
        completedSteps={ctx.progress?.steps}
        onUserInteraction={ctx.markStepDirty}
      >
        {readOnly && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Pas doar pentru citire"
            description="Acest pas este completat de alt rol din echipă. Poți consulta datele, dar nu le poți modifica."
          />
        )}
        <ConfigProvider componentDisabled={readOnly}>
          <StepComponent />
        </ConfigProvider>
      </WizardLayout>
    </div>
  );
};

const DermatoWizard = () => {
  const { id: patientId } = useParams<{ id: string }>();
  const location = useLocation();
  const editFormId = (location.state as { editFormId?: string } | null)?.editFormId;

  if (!patientId) {
    message.error('ID pacient lipsă');
    return null;
  }

  return (
    <DermatoWizardProvider patientId={patientId} editFormId={editFormId ?? null}>
      <DermatoWizardInner />
    </DermatoWizardProvider>
  );
};

export default DermatoWizard;
